import type { Location, Reference } from '@medplum/fhirtypes'
import { ExtendedLocation } from '@seen/core/models/location'
import { ExtendedOrganization } from '@seen/core/models/organizations/ExtendedOrganization'
import { Button } from '@seen/ui'
import { IconBuilding, IconMapPin, IconPencil } from '@tabler/icons-react'
import type { JSX } from 'react'
import { useRead } from '#src/hooks/useRead.ts'

export function LocationOption({
  location,
  onEdit,
  hideIcon,
}: {
  location: Reference<Location>
  onEdit?: (
    event: React.MouseEvent<HTMLButtonElement>,
    l: ExtendedLocation
  ) => void
  hideIcon?: boolean
}): JSX.Element {
  const { data: instance } = useRead(ExtendedLocation, location)
  const name = instance?.name
  const address = instance?.addressString
  // The managing organization identifies whose place this is (practice, ASC,
  // hospital) — the strongest cue for disambiguating a bare address.
  const { data: organization } = useRead(
    ExtendedOrganization,
    instance?.managingOrganization
  )
  const organizationName = organization?.name
  const isSame =
    name?.toLowerCase().replace(/\W/g, '') ===
    address?.toLowerCase().replace(/\W/g, '')
  // Don't repeat the org name when it's already the location's own name.
  const showOrganization =
    !!organizationName &&
    organizationName.toLowerCase().replace(/\W/g, '') !==
      name?.toLowerCase().replace(/\W/g, '')

  return (
    <div className="group flex flex-1 items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2">
        {!hideIcon && <IconMapPin className="size-4 shrink-0 p-0.5" />}
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-1">
            <p className="text-sm text-wrap">{name}</p>
            {!isSame && (
              <p className="text-xs text-muted-foreground">{address}</p>
            )}
          </div>
          {showOrganization && (
            <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
              <IconBuilding className="size-3 shrink-0" />
              {organizationName}
            </p>
          )}
        </div>
      </div>
      {onEdit && (
        <Button
          size="2xs"
          variant="ghost"
          onClick={(event) => {
            if (instance) {
              onEdit(event, instance)
            }
          }}
          className="opacity-0 group-hover:opacity-100"
        >
          <IconPencil size={14} className="text-muted-foreground" />
        </Button>
      )}
    </div>
  )
}
